import type { CrmNoteType } from '@plato/data/prisma';
import { WarningIcon } from '../../../assets/icons/warning-icon.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { api } from '../../../utils/api.ts';
import { BaseConfirmationDialog } from '../../dialog/confirmation-dialog.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import type { DialogInnerProps } from '../../dialog/types.ts';

type NoteDeleteDialogProps = {
    noteId: string;
    noteType?: CrmNoteType;
    onDeleted?: () => void;
} & DialogInnerProps;

export const NoteDeleteDialog = DialogStateManager.create<NoteDeleteDialogProps>(
    ({ noteId, noteType, onDeleted }) => {
        const { remove } = useDialog();
        const { t } = useLocale();
        const { toast } = useToast();
        const apiUtils = api.useUtils();

        // Get the appropriate toast title based on note type
        const getSuccessToastTitle = () => {
            switch (noteType) {
                case 'VISIT':
                    return t('notes__delete_confirmation__toast__success_title__event', { ns: 'notes' });
                case 'PHONE_CALL':
                    return t('notes__delete_confirmation__toast__success_title__phone_call', {
                        ns: 'notes',
                    });
                default:
                    return t('notes__delete_confirmation__toast__success_title', { ns: 'notes' });
            }
        };

        const getSuccessToastDescription = () => {
            switch (noteType) {
                case 'VISIT':
                    return t('notes__delete_confirmation__toast__success_description__event', {
                        ns: 'notes',
                    });
                case 'PHONE_CALL':
                    return t('notes__delete_confirmation__toast__success_description__phone_call', {
                        ns: 'notes',
                    });
                default:
                    return t('notes__delete_confirmation__toast__success_description', { ns: 'notes' });
            }
        };

        const deleteMutation = api.platoUser.command.notes.delete.useMutation({
            onSuccess: () => {
                remove();

                toast({
                    icon: 'success',
                    title: getSuccessToastTitle(),
                    description: getSuccessToastDescription(),
                });
                apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate();
                apiUtils.platoUser.query.customerActivity.getFeedList.getPinnedNotes.invalidate();

                onDeleted?.();
            },
            onError: (error) => {
                toast({
                    icon: 'warning',
                    title: 'Error',
                    description: error.message,
                });
            },
        });

        const handleDelete = () => {
            deleteMutation.mutate({ noteId });
        };

        // Get the appropriate title and description based on note type
        const getDeleteTitle = () => {
            switch (noteType) {
                case 'VISIT':
                    return t('notes__delete_confirmation__event__title', { ns: 'notes' });
                case 'PHONE_CALL':
                    return t('notes__delete_confirmation__phone_call__title', { ns: 'notes' });
                default:
                    return t('notes__delete_confirmation__title', { ns: 'notes' });
            }
        };

        const getDeleteDescription = () => {
            switch (noteType) {
                case 'VISIT':
                    return t('notes__delete_confirmation__event__description', { ns: 'notes' });
                case 'PHONE_CALL':
                    return t('notes__delete_confirmation__phone_call__description', { ns: 'notes' });
                default:
                    return t('notes__delete_confirmation__description', { ns: 'notes' });
            }
        };

        return (
            <BaseConfirmationDialog
                focus="destroy"
                icon={WarningIcon}
                title={getDeleteTitle()}
                description={getDeleteDescription()}
            >
                <BaseConfirmationDialog.Actions>
                    <BaseConfirmationDialog.RejectButton onClick={remove}>
                        {t('form__cancel')}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton
                        onClick={handleDelete}
                        isLoading={deleteMutation.isLoading}
                    >
                        {t('form__delete')}
                    </BaseConfirmationDialog.AcceptButton>
                </BaseConfirmationDialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
